import { useId } from 'react';
import { Text, View } from 'react-native';
import Svg, { ClipPath, Defs, G, Path, Rect } from 'react-native-svg';
import type { Shape } from '../store/types';
import { C, alpha } from './theme';
import { N_, t } from '../lib/i18n';

const D: Partial<Record<Shape, string>> = {
  hexagono: 'M50 4 L92 27 L92 73 L50 96 L8 73 L8 27 Z',
  bolinha: 'M50 4 A46 46 0 1 1 49.99 4 Z',
  estrela: 'M50 4 L61.8 36.4 L96 37.6 L69 58.6 L78.4 91.4 L50 72 L21.6 91.4 L31 58.6 L4 37.6 L38.2 36.4 Z',
  coracao: 'M50 90 C22 70 4 52 10 30 C16 10 40 8 50 26 C60 8 84 10 90 30 C96 52 78 70 50 90 Z',
  gota: 'M50 4 C62 22 86 44 86 64 A36 36 0 0 1 14 64 C14 44 38 22 50 4 Z',
  triangulo: 'M50 6 L96 90 L4 90 Z',
};
export const EMOJI_SHAPE: Partial<Record<Shape, string>> = {
  carro: '🚗', gato: '🐈', cachorro: '🐕', peixe: '🐟', tartaruga: '🐢', borboleta: '🦋', foguete: '🚀',
};
/** Emoji de uma forma divertida (as antigas e as do catálogo "e:🩺"). */
export const emojiDe = (s: Shape) => EMOJI_SHAPE[s] ?? (s.startsWith('e:') ? s.slice(2) : undefined);
// nome é getter: traduz no momento em que é lido.
export const FORMAS: { id: Shape; readonly nome: string }[] = [
  { id: 'hexagono', get nome() { return t('Hexágono'); } }, { id: 'bolinha', get nome() { return t('Bolinha'); } }, { id: 'estrela', get nome() { return t('Estrela'); } },
  { id: 'coracao', get nome() { return t('Coração'); } }, { id: 'gota', get nome() { return t('Gota'); } }, { id: 'triangulo', get nome() { return t('Triângulo'); } },
  { id: 'carro', get nome() { return t('Carro'); } }, { id: 'gato', get nome() { return t('Gato'); } }, { id: 'cachorro', get nome() { return t('Cachorro'); } }, { id: 'peixe', get nome() { return t('Peixe'); } },
  { id: 'tartaruga', get nome() { return t('Tartaruga'); } }, { id: 'borboleta', get nome() { return t('Borboleta'); } }, { id: 'foguete', get nome() { return t('Foguete'); } },
];

/** Emoji que vai ganhando cor de baixo para cima (usado nas formas divertidas). */
function EmojiFill({ e, pct, size }: { e: string; pct: number; size: number }) {
  const fs = size * 0.78;
  const glyph = <Text style={{ fontSize: fs, lineHeight: size, width: size, height: size, textAlign: 'center' }}>{e}</Text>;
  return (
    <View style={{ width: size, height: size }}>
      <View style={{ position: 'absolute', opacity: 0.22 }}>{glyph}</View>
      <View style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: size * pct, overflow: 'hidden' }}>
        <View style={{ position: 'absolute', bottom: 0 }}>{glyph}</View>
      </View>
    </View>
  );
}

/** Temporizador: forma que se enche de cor conforme o tempo da tarefa passa, com o ícone dentro. */
export function TimerShape({ shape, pct, color, size = 46, icon }: { shape: Shape; pct: number; color: string; size?: number; icon?: string }) {
  const id = 'c' + useId().replace(/[^a-zA-Z0-9]/g, '');
  const p = Math.max(0, Math.min(1, pct));
  const e = emojiDe(shape);
  if (e) return <EmojiFill e={e} pct={p} size={size} />;
  const d = D[shape] ?? D.hexagono!;
  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} viewBox="0 0 100 100" style={{ position: 'absolute' }}>
        <Defs><ClipPath id={id}><Path d={d} /></ClipPath></Defs>
        <Path d={d} fill={alpha(color, 0.12)} />
        <G clipPath={`url(#${id})`}><Rect x={0} y={100 * (1 - p)} width={100} height={100 * p} fill={alpha(color, 0.85)} /></G>
        <Path d={d} fill="none" stroke={color} strokeWidth={5} strokeLinejoin="round" />
      </Svg>
      {!!icon && <Text style={{ fontSize: size * 0.36 }}>{icon}</Text>}
    </View>
  );
}

/** Marcador de concluída (lado esquerdo): vazio ou cheio com ✓. */
export function DoneMark({ shape, done, color, size = 30 }: { shape: Shape; done: boolean; color: string; size?: number }) {
  const e = emojiDe(shape);
  if (e) return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ fontSize: size * 0.8, opacity: done ? 1 : 0.3 }}>{e}</Text>
      {done && <View style={{ position: 'absolute', right: -2, bottom: -2, backgroundColor: color, borderRadius: 8, width: 16, height: 16, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ color: '#fff', fontSize: 10, fontWeight: '800' }}>✓</Text></View>}
    </View>
  );
  const d = D[shape] ?? D.estrela!;
  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} viewBox="0 0 100 100" style={{ position: 'absolute' }}>
        <Path d={d} fill={done ? color : 'none'} stroke={done ? color : C.mute} strokeWidth={7} strokeLinejoin="round" />
      </Svg>
      {done && <Text style={{ color: '#fff', fontSize: size * 0.36, fontWeight: '900', marginTop: shape === 'triangulo' ? size * 0.15 : 0 }}>✓</Text>}
    </View>
  );
}

/** Catálogo de ícones por categoria (Ajustes > Abas > My Planner > Formatos). Nomes traduzidos na hora. */
type Item = { id: Shape; n: string };
const em = (e: string, n: string): Item => ({ id: `e:${e}`, n });
export const CATEGORIAS_ICONES: { titulo: string; itens: Item[] }[] = [
  { titulo: N_('Formas'), itens: [{ id: 'hexagono', n: N_('Hexágono') }, { id: 'bolinha', n: N_('Bolinha') }, { id: 'estrela', n: N_('Estrela') },
    { id: 'coracao', n: N_('Coração') }, { id: 'gota', n: N_('Gota') }, { id: 'triangulo', n: N_('Triângulo') }] },
  { titulo: N_('Bichos e diversão'), itens: [{ id: 'carro', n: N_('Carro') }, { id: 'gato', n: N_('Gato') }, { id: 'cachorro', n: N_('Cachorro') },
    { id: 'peixe', n: N_('Peixe') }, { id: 'tartaruga', n: N_('Tartaruga') }, { id: 'borboleta', n: N_('Borboleta') }, { id: 'foguete', n: N_('Foguete') },
    em('🦄', N_('Unicórnio')), em('🌸', N_('Flor')), em('🌙', N_('Lua')), em('☀️', N_('Sol')), em('🎵', N_('Música')), em('🎮', N_('Jogo'))] },
  { titulo: N_('Profissões'), itens: [em('🩺', N_('Médico')), em('🦷', N_('Dentista')), em('💅', N_('Esteticista')), em('💉', N_('Enfermeiro')),
    em('🧑‍🏫', N_('Professor')), em('⚖️', N_('Advogado')), em('🧑‍💻', N_('Tecnologia')), em('👷', N_('Engenharia')), em('🧑‍🍳', N_('Cozinha')),
    em('💇', N_('Cabeleireiro')), em('🐾', N_('Veterinário')), em('💼', N_('Escritório'))] },
  { titulo: N_('Estudos e exatas'), itens: [em('📚', N_('Livros')), em('📖', N_('Leitura')), em('🧮', N_('Calculadora')), em('📏', N_('Régua')),
    em('📐', N_('Esquadro')), em('🧪', N_('Tubo de ensaio')), em('🔬', N_('Microscópio')), em('✏️', N_('Lápis')), em('🎓', N_('Formatura')), em('🌎', N_('Geografia'))] },
  { titulo: N_('Atividades físicas e saúde'), itens: [em('🏋️', N_('Halteres')), em('🏃', N_('Corrida')), em('❤️', N_('Coração')), em('🧘', N_('Yoga')),
    em('🚴', N_('Bicicleta')), em('🏊', N_('Natação')), em('⚽', N_('Futebol')), em('🥗', N_('Alimentação')), em('💧', N_('Água')), em('😴', N_('Sono'))] },
  { titulo: N_('Casa e rotina'), itens: [em('🛋️', N_('Sofá')), em('🛏️', N_('Cama')), em('🧺', N_('Roupa')), em('🧹', N_('Limpeza')), em('🍳', N_('Fogão')),
    em('🔌', N_('Eletrodomésticos')), em('🔧', N_('Ferramentas')), em('🔨', N_('Martelo')), em('🪴', N_('Plantas')), em('🛒', N_('Mercado')), em('🏠', N_('Casa'))] },
  { titulo: N_('Finanças'), itens: [em('💲', N_('Cifrão')), em('👛', N_('Carteira')), em('🪙', N_('Moedas')), em('💳', N_('Cartão')), em('💰', N_('Dinheiro')),
    em('🏦', N_('Banco')), em('📈', N_('Investimento')), em('🐷', N_('Cofrinho'))] },
  { titulo: N_('Representatividade'), itens: [em('🏳️‍🌈', N_('Arco-íris')), em('🏳️‍⚧️', N_('Trans')), em('✊🏿', N_('Punho')), em('✊🏽', N_('Punho')),
    em('🤝', N_('União')), em('🫶', N_('Amor')), em('♿', N_('Acessibilidade')), em('☮️', N_('Paz')), em('🌍', N_('Mundo')), em('♀️', N_('Feminino')), em('♂️', N_('Masculino'))] },
];
